/**
 * Fails a GET that gets no response headers within `ms`, so a request stuck on
 * a dead connection (after sleep, a network change or a server restart) errors
 * and is retried instead of loading forever. Writes are never cut short: the
 * server may already have committed one, and a retry could repeat it. Once
 * headers arrive the body may take as long as it needs (the account export).
 */
export function withResponseTimeout(
  fetch: (request: Request) => Promise<Response>,
  ms: number,
): (request: Request) => Promise<Response> {
  return async (request) => {
    if (request.method !== "GET") return fetch(request);
    const controller = new AbortController();
    // The caller's own cancellation still applies, to the body as well.
    const forward = () => controller.abort(request.signal.reason);
    if (request.signal.aborted) forward();
    else request.signal.addEventListener("abort", forward, { once: true });
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, ms);
    try {
      return await fetch(new Request(request, { signal: controller.signal }));
    } catch (error) {
      // A TypeError, like any other network failure (see errorMessage).
      if (timedOut && !request.signal.aborted)
        throw new TypeError(`No response from the server within ${ms / 1000} s`, { cause: error });
      throw error;
    } finally {
      clearTimeout(timer);
    }
  };
}
